'use client';

import { useEffect, useEffectEvent, useState } from 'react';
import type { UsePinakesQueryParams, UsePinakesQueryResult } from './usePinakesQuery.types';

type TSettled<T> = { key: string; data: T | null; isError: boolean };

/**
 * Network-only read of Pinakes for a component (ADR-0011: controller + TanStack Query + effect).
 *
 * Pinakes cards are not stored in IndexedDB, so this is not `useLocalFirstQuery`: the controller's
 * service caches and deduplicates through `pinakesQueryClient`, and this hook only tracks the
 * answer for the current key. A disabled read (no input, Pinakes not configured, not opened yet)
 * makes no request and reads as settled `null`.
 */
export function usePinakesQuery<T>({ key, fetchFn, enabled }: UsePinakesQueryParams<T>): UsePinakesQueryResult<T> {
  const [settled, setSettled] = useState<TSettled<T> | null>(null);
  const runFetch = useEffectEvent(fetchFn);

  useEffect(() => {
    if (!enabled) return;

    // Set by the cleanup when the key changes or the component unmounts, so an answer that lost
    // the race never replaces a newer one.
    let cancelled = false;
    runFetch().then(
      (data) => {
        if (!cancelled) setSettled({ key, data, isError: false });
      },
      () => {
        // The service already reported the failure through Err.*; a card is optional, so the
        // caller shows its error or fallback state and nothing is logged again here.
        if (!cancelled) setSettled({ key, data: null, isError: true });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [key, enabled]);

  if (!enabled) return { data: null, isLoading: false, isError: false };

  const current = settled?.key === key ? settled : null;
  if (!current) return { data: undefined, isLoading: true, isError: false };

  return { data: current.data, isLoading: false, isError: current.isError };
}
